/**
 * `InlineFieldEditor` — one editable entity field: hover-Edit display (the
 * member page's `profile-field` chrome) → inline editor → a ONE-FIELD canonical
 * dispatch → `onSaved` refetch.
 *
 * Was `PrFieldEditor`, which hardcoded `update_outreach_target`. It is now
 * parameterised by `action` + `idField` because `/inbox/threads/:id` edits the
 * MATTER (`update_inbox_thread`) as well as the person, and a component named
 * for one board that writes another's table is the misleading name the naming
 * convention exists to prevent.
 *
 * Still shared by `/pr/people/:id` AND `/inbox/threads/:id` for the person
 * grid — the inbox matter's person IS the same `outreach_targets` row Peter
 * edits, so both pages write through the same one-field dispatch or an edit
 * made on one board would need re-making on the other.
 *
 * CLEARING IS PER-ACTION, and the discriminator is load-bearing:
 * `update_outreach_target` clears with `''`; `update_inbox_thread` clears with
 * an explicit `null` and REJECTS `''` outright (its prose fields carry
 * `minLength: 1`, `urgency` is an enum). `spec.clearTo` carries which — see
 * `lib/pr-person-edit.ts`.
 */

import { useState } from "react";
import { useDispatch } from "../lib/use-dispatch";
import type { PrEditFieldSpec } from "../lib/pr-person-edit";
import { parsePrEditDraft } from "../lib/pr-person-edit";

export function InlineFieldEditor({
  entityId,
  action,
  idField,
  spec,
  value,
  display,
  onSaved,
}: {
  /** The row being patched — locked, never typed by the operator. */
  readonly entityId: string;
  /** Canonical action this field dispatches (one field per call). */
  readonly action: string;
  /** The action's id input key, e.g. `outreach_target_id` / `inbox_thread_id`. */
  readonly idField: string;
  readonly spec: PrEditFieldSpec;
  /** Current stored value, editor-shaped ('' when unset). */
  readonly value: string;
  /** Read-view rendering (defaults to the value or an em-dash). */
  readonly display?: string | undefined;
  readonly onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [parseError, setParseError] = useState<string | null>(null);
  const dispatch = useDispatch<
    Record<string, string | number | null>,
    Record<string, unknown>
  >(action);

  const begin = (): void => {
    setDraft(value);
    setParseError(null);
    dispatch.reset();
    setEditing(true);
  };
  const cancel = (): void => {
    setParseError(null);
    dispatch.reset();
    setEditing(false);
  };
  const save = async (): Promise<void> => {
    if (dispatch.loading) return;
    const parsed = parsePrEditDraft(spec, draft);
    if (!parsed.ok) {
      setParseError(parsed.message);
      return;
    }
    setParseError(null);
    const r = await dispatch.run({
      [idField]: entityId,
      [spec.field]: parsed.value,
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  const shown = display ?? (value.trim().length > 0 ? value : "—");
  if (!editing) {
    return (
      <div className="profile-field">
        <span className="profile-field-val">
          <strong>{spec.label}:</strong> {shown}
        </span>
        <button
          type="button"
          className="profile-field-edit"
          title={`Edit ${spec.label}`}
          onClick={begin}
        >
          Edit
        </button>
      </div>
    );
  }

  const editor =
    spec.kind === "select" ? (
      <select
        className="profile-field-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      >
        {/* A clearable select needs a way BACK to unset — without this an
            operator who sets urgency can never remove it again. */}
        {spec.clearable ? <option value="">— none —</option> : null}
        {(spec.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    ) : spec.kind === "multiline" ? (
      <textarea
        className="profile-field-input"
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") cancel();
        }}
      />
    ) : (
      <input
        className="profile-field-input"
        type={spec.kind === "number" ? "number" : "text"}
        value={draft}
        placeholder={spec.placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") cancel();
        }}
      />
    );

  const err = parseError ?? dispatch.error?.message ?? null;
  return (
    <div className="profile-field profile-field-editing">
      <div className="profile-edit-row">
        {editor}
        <button
          type="button"
          className="action-dispatch-btn profile-field-save"
          disabled={dispatch.loading}
          onClick={() => void save()}
        >
          {dispatch.loading ? "…" : "Save"}
        </button>
        <button
          type="button"
          className="profile-field-cancel"
          disabled={dispatch.loading}
          onClick={cancel}
        >
          Cancel
        </button>
      </div>
      {err !== null ? <div className="flag-card-err">{err}</div> : null}
    </div>
  );
}
